import React, {useEffect, useMemo, useState} from "react";
import styles from "./LeaderboardModal.module.scss";
import {getCurrentUserId} from "../../../../api/authContext";
import {UserStatsRecordDto} from "../../../types/user";
import {findTop20ByAvgGamePoints, findTop20ByGamesPlayed, findTop20ByGamesWon} from "../../../../api/profile";

type TabKey = "games" | "wins" | "avg";

type Props = {
    open: boolean;
    onClose: () => void;
    initialTab?: TabKey;
};

function tabTitle(tab: TabKey) {
    switch (tab) {
        case "games":
            return "Games played";
        case "wins":
            return "Games won";
        case "avg":
            return "Avg points per game";
    }
}

function formatValue(tab: TabKey, value: number) {
    if (tab === "avg") return value.toFixed(2);
    return String(value);
}

const LeaderboardModal: React.FC<Props> = ({open, onClose, initialTab = "games"}) => {
    const currentUserId = getCurrentUserId();

    const [tab, setTab] = useState<TabKey>(initialTab);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<UserStatsRecordDto[]>([]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                let res: UserStatsRecordDto[] = [];
                if (tab === "games") res = await findTop20ByGamesPlayed();
                if (tab === "wins") res = await findTop20ByGamesWon();
                if (tab === "avg") res = await findTop20ByAvgGamePoints();
                setRows(res ?? []);
            } catch (e) {
                setError("Failed to load leaderboard.");
                setRows([]);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [open, tab]);

    const {topRows, myRow} = useMemo(() => {
        const my = rows.find(r => r.userId === currentUserId) ?? null;

        const top = rows
            .filter(r => r.rank <= 20)
            .sort((a, b) => a.rank - b.rank || a.username.localeCompare(b.username));

        const mySeparate = my && my.rank > 20 ? my : null;

        return {topRows: top, myRow: mySeparate};
    }, [rows, currentUserId]);

    if (!open) return null;

    return (
        <div className={styles.backdrop} onMouseDown={onClose} role="presentation">
            <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="leaderboard-title"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className={styles.header}>
                    <div>
                        <h2 id="leaderboard-title" className={styles.title}>Leaderboard</h2>
                        <div className={styles.subtitle}>Top players + your position</div>
                    </div>
                </div>

                <div className={styles.tabs} role="tablist" aria-label="Leaderboard tabs">
                    {(["games", "wins", "avg"] as TabKey[]).map(t => (
                        <button
                            key={t}
                            className={`${styles.tabBtn} ${tab === t ? styles["tabBtn--active"] : ""}`}
                            onClick={() => setTab(t)}
                            role="tab"
                            aria-selected={tab === t}
                        >
                            {tabTitle(t)}
                        </button>
                    ))}
                </div>

                <div className={styles.body}>
                    {loading && <div className={styles.state}>Loading...</div>}
                    {!loading && error && <div className={styles.stateError}>{error}</div>}

                    {!loading && !error && (
                        <>
                            {topRows.length === 0 ? (
                                <div className={styles.state}>No stats yet.</div>
                            ) : (
                                <div className={styles.tableWrap}>
                                    <table className={styles.table}>
                                        <thead>
                                        <tr>
                                            <th className={styles.colRank}>#</th>
                                            <th className={styles.colUser}>Player</th>
                                            <th className={styles.colValue}>{tabTitle(tab)}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {topRows.map(r => {
                                            const isMe = r.userId === currentUserId;
                                            return (
                                                <tr key={`${r.userId}-${r.rank}`}
                                                    className={isMe ? styles.rowMe : undefined}>
                                                    <td className={styles.colRank}>{r.rank}</td>
                                                    <td className={styles.colUser}>
                                                        <span className={styles.userName}>{r.username}</span>
                                                        {isMe && <span className={styles.meBadge}>you</span>}
                                                    </td>
                                                    <td className={styles.colValue}>{formatValue(tab, r.value)}</td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {myRow && (
                                <>
                                    <div className={styles.divider}/>
                                    <div className={styles.yourBlockTitle}>Your position</div>

                                    <div className={styles.tableWrap}>
                                        <table className={styles.table}>
                                            <thead>
                                            <tr>
                                                <th className={styles.colRank}>#</th>
                                                <th className={styles.colUser}>Player</th>
                                                <th className={styles.colValue}>{tabTitle(tab)}</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr className={styles.rowMe}>
                                                <td className={styles.colRank}>{myRow.rank}</td>
                                                <td className={styles.colUser}>
                                                    <span className={styles.userName}>{myRow.username}</span>
                                                    <span className={styles.meBadge}>you</span>
                                                </td>
                                                <td className={styles.colValue}>{formatValue(tab, myRow.value)}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                <div className={styles.footer}>
                    <button className={styles.secondaryBtn} onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default LeaderboardModal;
